import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './StoryboardThumbPreview.css';

interface StoryboardThumbPreviewProps {
  thumbnailUrl: string;
  /** ホバー時にポップアップ表示する高解像度画像URL */
  highResUrl?: string;
  alt?: string;
}

/**
 * ストーリーボードのサムネイル。ホバーすると高解像度画像をポップアップ表示する。
 * - 高解像度画像のダウンロードが完了してから表示する（待ち時間中はポップアップしない）
 * - ホバーを外すと消える（Xボタンなし）
 */
export function StoryboardThumbPreview({ thumbnailUrl, highResUrl, alt = 'ストーリーボード' }: StoryboardThumbPreviewProps) {
  const [hovering, setHovering] = useState(false);
  const [ready, setReady] = useState(false);
  const [coords, setCoords] = useState<{ left: number; top: number } | null>(null);
  const thumbRef = useRef<HTMLImageElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<DOMRect | null>(null);

  // ホバー中に高解像度画像を先読みし、ダウンロード完了後に表示可能にする
  useEffect(() => {
    if (!hovering || ready || !highResUrl) return;
    const img = new Image();
    let cancelled = false;
    img.onload = () => { if (!cancelled) setReady(true); };
    img.src = highResUrl;
    return () => { cancelled = true; };
  }, [hovering, ready, highResUrl]);

  const visible = hovering && ready && !!highResUrl;

  // ポップアップ描画後に位置を確定（サムネイル横、画面外にはみ出さないようクランプ）
  useLayoutEffect(() => {
    if (!visible || !popupRef.current || !rectRef.current) return;
    const rect = rectRef.current;
    const pop = popupRef.current.getBoundingClientRect();
    const gap = 12;
    let left = rect.right + gap;
    if (left + pop.width > window.innerWidth - 8) {
      left = rect.left - pop.width - gap;
    }
    left = Math.max(8, left);
    // 上下は画面中央に固定
    const top = Math.max(8, (window.innerHeight - pop.height) / 2);
    setCoords({ left, top });
  }, [visible]);

  const handleEnter = () => {
    if (thumbRef.current) rectRef.current = thumbRef.current.getBoundingClientRect();
    setHovering(true);
  };
  const handleLeave = () => {
    setHovering(false);
    setCoords(null);
  };

  return (
    <>
      <img
        ref={thumbRef}
        src={thumbnailUrl}
        alt={alt}
        loading="lazy"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      />
      {visible && createPortal(
        <div
          ref={popupRef}
          className="storyboard-thumb-preview-popup"
          style={{
            left: coords?.left ?? 0,
            top: coords?.top ?? 0,
            visibility: coords ? 'visible' : 'hidden',
          }}
        >
          <img src={highResUrl} alt={alt} />
        </div>,
        document.body
      )}
    </>
  );
}
